/**
 * SPEC §9.1 — MapLibre globe + deck.gl interleaved, on a Protomaps PMTiles
 * basemap served by our own Worker. The globe projection is part of the style,
 * so it is in force before the overlay is attached: attaching in Mercator and
 * switching later leaves deck.gl layers deformed.
 */
import { Map as MapLibreMap, NavigationControl, addProtocol, type StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Protocol } from 'pmtiles';
import { layers, namedFlavor } from '@protomaps/basemaps';
import { MapboxOverlay } from '@deck.gl/mapbox';
import type { Theme } from '@latency-globe/shared';
import { reducedMotion } from './store.ts';

let protocolRegistered = false;

export function flavorFor(theme: Theme) {
  return namedFlavor(theme === 'dark' ? 'dark' : 'light');
}

export function buildStyle(theme: Theme): StyleSpecification {
  const name = theme === 'dark' ? 'dark' : 'light';
  return {
    version: 8,
    projection: { type: 'globe' },
    glyphs: 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf',
    sprite: `https://protomaps.github.io/basemaps-assets/sprites/v4/${name}`,
    sources: {
      protomaps: {
        type: 'vector',
        url: `pmtiles://${location.origin}/tiles/basemap.pmtiles`,
        attribution: '© <a href="https://openstreetmap.org/copyright">OpenStreetMap</a> · <a href="https://protomaps.com">Protomaps</a>',
      },
    },
    layers: layers('protomaps', flavorFor(theme), { lang: 'en' }),
  } as StyleSpecification;
}

export function hasWebGL2(): boolean {
  try { return !!document.createElement('canvas').getContext('webgl2'); } catch { return false; }
}

export interface Globe {
  map: MapLibreMap;
  overlay: MapboxOverlay;
  /** id of the first symbol layer — deck layers go beneath it so labels stay legible */
  labelLayerId(): string | undefined;
  setTheme(theme: Theme): Promise<void>;
  stopIdleSpin(): void;
}

export function createGlobe(container: HTMLElement, theme: Theme, onReady: () => void): Globe {
  if (!protocolRegistered) { addProtocol('pmtiles', new Protocol().tile); protocolRegistered = true; }
  const map = new MapLibreMap({
    container,
    style: buildStyle(theme),
    center: [-30, 25],
    zoom: 1.6,
    minZoom: 0.8,
    maxZoom: 13,
    attributionControl: { compact: true },
    canvasContextAttributes: { antialias: true },
  });
  map.addControl(new NavigationControl({ visualizePitch: false }), 'top-right');
  map.keyboard.enable();

  const overlay = new MapboxOverlay({ interleaved: true, layers: [] });
  let ready = false;
  map.on('load', () => {
    map.addControl(overlay);
    ready = true;
    onReady();
  });

  // A slow idle rotation until the first interaction; never under reduced motion.
  let spinning = !reducedMotion();
  let raf = 0;
  const spin = () => {
    if (!spinning) return;
    if (map.getZoom() < 3 && !map.isMoving()) {
      const c = map.getCenter();
      map.setCenter([c.lng + 0.04, c.lat]);
    }
    raf = requestAnimationFrame(spin);
  };
  const stopIdleSpin = () => { spinning = false; cancelAnimationFrame(raf); };
  for (const ev of ['mousedown', 'touchstart', 'wheel', 'keydown'] as const) container.addEventListener(ev, stopIdleSpin, { once: true, passive: true });
  map.once('load', () => { raf = requestAnimationFrame(spin); });

  const labelLayerId = () => map.getStyle()?.layers.find((l: { type: string }) => l.type === 'symbol')?.id;

  const setTheme = (t: Theme) => new Promise<void>((resolve) => {
    if (!ready) { resolve(); return; }
    map.once('style.load', () => resolve());
    map.setStyle(buildStyle(t), { diff: false });
  });

  return { map, overlay, labelLayerId, setTheme, stopIdleSpin };
}
